/**
 * Faro sampling rate
 *
 * Faro sends signals for only a share of sessions, and nothing in the signals says
 * which share. Dashboards that turn counts into estimates divide by that rate, so it
 * has to travel with the data: a change to it (in `@opengov/psc-react-logger`, which
 * sets it today, or in this shell) would otherwise skew every estimate silently.
 *
 * This instrumentation reads the rate from the Faro config it is registered with and
 * adds it as the `sampling_rate` session attribute on every item Faro sends with a session
 * (`session_attr_sampling_rate` in Loki). Items without one, sent only while session tracking
 * is off, aren't sampled out and are left as they are.
 */

import { BaseInstrumentation, type TransportItem } from '@grafana/faro-web-sdk';
import { SAMPLING_RATE_ATTRIBUTE, SESSION_SAMPLING_RATE_KEY } from './faroSamplingRateKeys';

interface SessionSamplingRate {
  sessionId: string;
  rate: number;
}

function isSessionSamplingRate(value: unknown): value is SessionSamplingRate {
  return (
    typeof value === 'object' &&
    value !== null &&
    'sessionId' in value &&
    typeof value.sessionId === 'string' &&
    'rate' in value &&
    typeof value.rate === 'number' &&
    value.rate >= 0 &&
    value.rate <= 1
  );
}

export class FaroSamplingRateInstrumentation extends BaseInstrumentation {
  readonly name = '@opengov/psp-web:sampling-rate';
  readonly version = '1.0.0';

  initialize(): void {
    // An instrumentation that throws during init stops the shell rendering (see main.tsx), so a
    // change in Faro's shape should cost this attribute, not the page.
    try {
      this.addSamplingRateHook();
    } catch (error) {
      this.logWarn('Sampling rate not recorded', error);
    }
  }

  private addSamplingRateHook(): void {
    const currentRate = this.samplingRate();
    const rateFor = this.sessionRateLookup(currentRate);
    // A before-send hook rather than the session data: with batching on, Faro runs the hooks
    // when a batch is sent, which also covers the `session_start` the session instrumentation
    // pushes on startup. Hooks run in the order they were added, and this one must run after
    // the session instrumentation's, which can replace an item's session with a rotated one.
    this.transports.addBeforeSendHooks((item: TransportItem) => {
      const { session } = item.meta;
      // No session means session tracking is off, so nothing is sampled out; adding one here
      // would only send a session with no id.
      if (!session) return item;
      const rate = session.id ? rateFor(session.id) : currentRate;
      return {
        ...item,
        meta: {
          ...item.meta,
          session: {
            ...session,
            attributes: { ...session.attributes, [SAMPLING_RATE_ATTRIBUTE]: String(rate) },
          },
        },
      };
    });
  }

  /**
   * The rate each session was sampled at. Faro resumes a recent session after a reload and keeps
   * the sampling decision it made when the session started, so after a rate change a resumed
   * session must report the earlier rate. The rate is stored with the session's id in the
   * storage Faro keeps the session in, and a session this page load starts gets the current
   * rate. A session resumed from before this was recorded gets the current rate too.
   *
   * Storage is read again whenever the session changes, not only at startup: with persistent
   * sessions every tab shares it, and a tab can adopt a session another tab started, whose rate
   * that tab has already stored.
   */
  private sessionRateLookup(currentRate: number): (sessionId: string) => number {
    const storage = this.sessionStorageArea();
    let known: SessionSamplingRate | undefined;
    return (sessionId) => {
      if (known?.sessionId === sessionId) return known.rate;
      const stored = readSessionRate(storage);
      if (stored?.sessionId === sessionId) {
        known = stored;
        return stored.rate;
      }
      known = { sessionId, rate: currentRate };
      writeSessionRate(storage, known);
      return currentRate;
    };
  }

  private sessionStorageArea(): Storage | undefined {
    try {
      return this.config.sessionTracking?.persistent ? window.localStorage : window.sessionStorage;
    } catch {
      return undefined;
    }
  }

  /**
   * The rate Faro samples sessions at, worked out the way Faro does it, once per page load.
   * A custom `sampler` that returns different rates for later sessions would make this stale.
   * The sampling config comes from `@opengov/psc-react-logger`, not the shell, and it sets a
   * fixed `samplingRate` with no sampler today.
   */
  private samplingRate(): number {
    const tracking = this.config.sessionTracking;
    // Without session tracking Faro samples nothing out.
    if (!tracking?.enabled) return 1;
    const rate = tracking.sampler?.({ metas: this.metas.value }) ?? tracking.samplingRate ?? 1;
    // As Faro does: clamp any number (so Infinity samples every session), and sample none
    // for NaN or a value that isn't a number.
    return typeof rate === 'number' && !Number.isNaN(rate) ? Math.min(Math.max(rate, 0), 1) : 0;
  }
}

function readSessionRate(storage: Storage | undefined): SessionSamplingRate | undefined {
  try {
    const stored: unknown = JSON.parse(storage?.getItem(SESSION_SAMPLING_RATE_KEY) ?? 'null');
    return isSessionSamplingRate(stored) ? stored : undefined;
  } catch {
    return undefined;
  }
}

function writeSessionRate(storage: Storage | undefined, value: SessionSamplingRate): void {
  try {
    storage?.setItem(SESSION_SAMPLING_RATE_KEY, JSON.stringify(value));
  } catch {
    // Storage full or blocked: the in-memory value still covers this page load.
  }
}
